import { Component, Input, ChangeDetectionStrategy } from '@angular/core';
import { LoggerService } from '../../../../../../../services/logger.service';
import { CommonModule } from '@angular/common';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';

// Shared business hours type
export interface ContactBusinessHours {
  day?: string;
  days?: string; // API format: "Monday - Friday"
  time?: string; // API format: "8:00 AM - 6:00 PM"
  openTime?: string;
  closeTime?: string;
  closed?: boolean;
}

export interface ContactLayout1Data {
  title?: string;
  subtitle?: string;
  address?: string;
  phone?: string;
  email?: string;
  mapEmbedUrl?: string;
  mapImage?: string;
  mapAlt?: string;
  businessHours?: ContactBusinessHours[];
  backgroundColor?: string;
}

/**
 * Preview component for "Contact Layout 1" used in the website builder; renders contact details, optional business hours, and a safely-embedded map preview while applying provided styling overrides.
 * @component
 * @selector app-contact-layout-1-preview
 * @example
 *   <app-contact-layout-1-preview
 *     [data]="contactData"
 *     [headingColor]="'#1a1a1a'"
 *     [paragraphColor]="'#333333'"
 *     [headingFont]="'Roboto, sans-serif'"
 *     [bodyFont]="'Open Sans, sans-serif'"
 *     [primaryColor]="'#007bff'"
 *     [titleStyles]="{ 'font-weight': '600' }">
 *   </app-contact-layout-1-preview>
 * @input {ContactLayout1Data | null} data - Primary data model for the contact layout (address, phone, email, businessHours array, mapEmbedUrl, and other content used to render the preview).
 * @input {string | null} headingColor - Optional color value applied to headings in the preview.
 * @input {string | null} paragraphColor - Optional color value applied to paragraph/body text.
 * @input {string | null} headingFont - Optional font-family for headings.
 * @input {string | null} bodyFont - Optional font-family for body text.
 * @input {string | null} primaryColor - Optional primary theme/accent color used by the layout.
 * @input {any} titleStyles - Optional inline style object applied to title elements (allowing ad-hoc style overrides).
 * @output {void} none - This component is presentational and does not emit output events.
 *
 * Key behavior and methods:
 * - Constructor injects DomSanitizer to safely create resource URLs for map embeds.
 * - hasBusinessHours(): boolean — returns true when businessHours exist and contain at least one entry.
 * - getMapEmbedUrl(): SafeResourceUrl | null — robustly derives and caches a safe embed URL from the provided mapEmbedUrl string. Handles plain http(s) URLs, iframe HTML snippets (extracts src), trims/cleans HTML-encoded characters, rejects unsafe schemes (javascript:, data:, etc.), and caches the sanitized result to avoid flicker.
 *
 * Important implementation notes / lifecycle:
 * - The component is standalone and used as a preview; no Angular lifecycle hooks are required or implemented for normal operation.
 * - Internal caching fields (cachedMapEmbedUrl and cachedMapEmbedUrlSource) are used to avoid re-sanitization and flicker when the map URL is unchanged.
 * - getMapEmbedUrl contains defensive parsing with try/catch to avoid breaking the preview when malformed HTML or unexpected values are provided.
 */
@Component({
  selector: 'app-contact-layout-1-preview',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './contact-layout-1.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./contact-layout-1.component.scss'],
})
export class ContactLayout1PreviewComponent {
  @Input() data: ContactLayout1Data | null = null;
  @Input() headingColor: string | null = null;
  @Input() paragraphColor: string | null = null;
  @Input() headingFont: string | null = null;
  @Input() bodyFont: string | null = null;
  @Input() primaryColor: string | null = null;
  @Input() titleStyles: any = {};

  // Cache for map embed URL to prevent flickering
  private cachedMapEmbedUrl: SafeResourceUrl | null = null;
  private cachedMapEmbedUrlSource: string | null = null;

  constructor(
    private logger: LoggerService,
    private sanitizer: DomSanitizer
  ) {}

  hasBusinessHours(): boolean {
    return !!(
      this.data?.businessHours &&
      Array.isArray(this.data.businessHours) &&
      this.data.businessHours.length > 0
    );
  }

  /**
   * Returns a sanitized SafeResourceUrl for the contact panel's map embed URL, or null if the URL is missing, invalid, or disallowed.
   * @example
   * etMapEmbedUrl()
   * SafeResourceUrl | null
   * @param {{void}} none - No arguments.
   * @returns {{SafeResourceUrl|null}} Sanitized SafeResourceUrl for embedding the map or null if unavailable/invalid.
   **/
  getMapEmbedUrl(): SafeResourceUrl | null {
    const mapEmbedUrl = this.data?.mapEmbedUrl;

    if (!mapEmbedUrl || typeof mapEmbedUrl !== 'string') {
      this.cachedMapEmbedUrl = null;
      this.cachedMapEmbedUrlSource = null;
      return null;
    }

    const trimmedUrl = mapEmbedUrl.trim();

    // Check if we have a cached value for this URL
    if (this.cachedMapEmbedUrl && this.cachedMapEmbedUrlSource === trimmedUrl) {
      return this.cachedMapEmbedUrl;
    }

    // Process the URL
    let processedUrl: string | null = trimmedUrl;

    // If it's already a clean URL (starts with http:// or https://), use it directly
    if (trimmedUrl.startsWith('http://') || trimmedUrl.startsWith('https://')) {
      processedUrl = trimmedUrl;
    }
    // If it contains iframe HTML, extract the src URL
    else if (trimmedUrl.includes('<iframe') || trimmedUrl.includes('&lt;iframe')) {
      try {
        const srcMatch = trimmedUrl.match(/src\s*=\s*"([^"]+)"|src\s*=\s*'([^']+)'/i);
        if (srcMatch) {
          const extractedUrl = (srcMatch[1] || srcMatch[2] || '').trim();
          if (extractedUrl) {
            processedUrl = extractedUrl
              .replace(/&quot;/g, '"')
              .replace(/&#39;/g, "'")
              .replace(/&amp;/g, '&');
          }
        }

        if (!processedUrl || processedUrl === trimmedUrl) {
          const wrappedHtml = `<div>${trimmedUrl}</div>`;
          const parser = new DOMParser();
          const doc = parser.parseFromString(wrappedHtml, 'text/html');
          const iframe = doc.querySelector('iframe');
          if (iframe) {
            processedUrl = iframe.getAttribute('src');
          }
        }
      } catch (error) {
        this.logger.warn('Error extracting URL from iframe HTML:', error);
        this.cachedMapEmbedUrl = null;
        this.cachedMapEmbedUrlSource = null;
        return null;
      }
    }

    if (!processedUrl) {
      this.cachedMapEmbedUrl = null;
      this.cachedMapEmbedUrlSource = null;
      return null;
    }

    // Only allow http(s) URLs — reject javascript:, data:, or any other scheme
    if (!processedUrl.startsWith('https://') && !processedUrl.startsWith('http://')) {
      this.cachedMapEmbedUrl = null;
      this.cachedMapEmbedUrlSource = null;
      return null;
    }

    this.cachedMapEmbedUrl = this.sanitizer.bypassSecurityTrustResourceUrl(processedUrl);
    this.cachedMapEmbedUrlSource = trimmedUrl;

    return this.cachedMapEmbedUrl;
  }

  trackByIndex(index: number): number {
    return index;
  }
  trackByValue(_: number, item: any): any {
    return item?.value ?? item;
  }
  trackById(_: number, item: any): any {
    return item?.id ?? item;
  }
  trackByString(_: number, s: any): any {
    return s;
  }
}
